import React, { useMemo, useState } from 'react';
import { FaStar } from 'react-icons/fa';

import { useAuth } from '../../contexts/AuthContext';
import { AuthModal } from '../auth';

interface OptimizedFavoriteStarButtonProps {
  dinosaurId?: string;
  articleId?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const OptimizedFavoriteStarButton: React.FC<OptimizedFavoriteStarButtonProps> = ({
  dinosaurId,
  articleId,
  className = '',
  size = 'md',
  position = 'top-right',
}) => {
  const { user, toggleFavorite, isFavorited } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // サイズクラスの定義
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  // 位置クラスの定義
  const positionClasses = {
    'top-right': 'top-2 right-2',
    'top-left': 'top-2 left-2',
    'bottom-right': 'bottom-2 right-2',
    'bottom-left': 'bottom-2 left-2',
  };

  // アイコンサイズの定義
  const iconSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    // イベントの伝播を止める（親要素のクリックイベントが発火しないようにする）
    e.preventDefault();
    e.stopPropagation();

    if (!user?.id) {
      // ログインが必要な場合の処理 - ログインモーダルを表示
      setShowAuthModal(true);
      return;
    }

    const targetId = dinosaurId || articleId;
    const targetType = dinosaurId ? 'creature' : 'article';

    if (!targetId) return;

    await toggleFavorite(targetId, targetType);
  };

  const targetId = dinosaurId || articleId;
  const targetType = dinosaurId ? 'creature' : 'article';

  // お気に入り状態をAuthContextのisFavorited関数で確認
  const favorited = useMemo(() => {
    if (!targetId) return false;
    return isFavorited(targetId, targetType);
  }, [targetId, targetType, isFavorited]);

  // ログインしていない場合は、お気に入り状態をfalseとして表示
  if (!user?.id) {
    return (
      <>
        <button
          className={`
            absolute ${positionClasses[position]}
            ${sizeClasses[size]}
            bg-white rounded-full shadow-md
            flex items-center justify-center
            transition-all duration-200
            hover:shadow-lg hover:scale-110
            z-10
            ${className}
          `}
          aria-label="お気に入りに追加（ログインが必要）"
          data-testid="favorite-star-button"
          data-creature-id={dinosaurId}
          data-article-id={articleId}
          onClick={handleToggleFavorite}
        >
          <FaStar
            className={`
              ${iconSizes[size]}
              transition-colors duration-200
              text-gray-400
            `}
          />
        </button>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="login"
          onSuccess={() => {
            setShowAuthModal(false);
          }}
        />
      </>
    );
  }

  return (
    <button
      onClick={handleToggleFavorite}
      className={`
        absolute ${positionClasses[position]}
        ${sizeClasses[size]}
        bg-white rounded-full shadow-md
        flex items-center justify-center
        transition-all duration-200
        hover:shadow-lg hover:scale-110
        z-10
        ${className}
      `}
      aria-label={favorited ? 'お気に入りから削除' : 'お気に入りに追加'}
      data-testid="favorite-star-button"
      data-creature-id={dinosaurId}
      data-article-id={articleId}
    >
      <FaStar
        className={`
          ${iconSizes[size]}
          transition-colors duration-200
          ${favorited ? 'text-yellow-500' : 'text-gray-400'}
        `}
      />
    </button>
  );
};

export default OptimizedFavoriteStarButton;
